// Field behavior copied from the storefront AuthRegistration components.

import React, { useEffect, useLayoutEffect, useRef, useState } from 'react'
import {
  UA_NATIONAL_LENGTH,
  UA_PHONE_PLACEHOLDER,
  buildUaPhoneValue,
  caretIndexForDigitCount,
  countDigitsBeforeIndex,
  extractUaNationalDigits,
  formatUaNationalDisplay,
} from '../validation/phoneValidation'
import styles from '../AuthRegistration.module.scss'

/**
 * UA national phone: +380 outside, progressive mask inside: 68 899 97 99
 * Digits stay in React state; caret mapped by digit count (append-friendly for Android).
 */
export default function AuthPhoneField({
  id,
  label,
  nationalDigits,
  onChange,
  error,
  disabled,
  autoFocus,
  onBlurValidate,
  hideErrorMessage = false,
}) {
  const inputRef = useRef(null)
  const pendingCaretDigitsRef = useRef(null)
  const [focused, setFocused] = useState(false)

  const digits = extractUaNationalDigits(nationalDigits)
  const display = formatUaNationalDisplay(digits)
  const hasValue = Boolean(digits)
  const errorId = error ? `${id}-error` : undefined
  const showFloatingLabel = focused || hasValue

  useLayoutEffect(() => {
    const input = inputRef.current
    if (!input || pendingCaretDigitsRef.current == null) return
    const caret = caretIndexForDigitCount(display, pendingCaretDigitsRef.current)
    try {
      input.setSelectionRange(caret, caret)
    } catch {
      // Some mobile WebViews throw if input is not ready.
    }
    pendingCaretDigitsRef.current = null
  }, [display])

  useEffect(() => {
    if (!autoFocus) return undefined
    const t = window.setTimeout(() => inputRef.current?.focus(), 50)
    return () => window.clearTimeout(t)
  }, [autoFocus])

  return (
    <div className={styles.fieldBlock}>
      <div
        className={[
          styles.field,
          styles.phoneControl,
          showFloatingLabel ? styles.fieldWithValue : '',
          focused ? styles.fieldFocused : '',
          error ? styles.fieldInvalid : '',
        ]
          .filter(Boolean)
          .join(' ')}
      >
        <span className={styles.phonePrefix} aria-hidden>
          +380
        </span>
        <input
          ref={inputRef}
          id={id}
          name="auth-ua-national-phone"
          type="text"
          inputMode="numeric"
          autoComplete="off"
          autoCorrect="off"
          autoCapitalize="off"
          spellCheck={false}
          value={display}
          disabled={disabled}
          aria-invalid={error ? true : undefined}
          aria-describedby={!hideErrorMessage ? errorId : undefined}
          placeholder={showFloatingLabel ? UA_PHONE_PLACEHOLDER : ' '}
          enterKeyHint="done"
          onFocus={() => setFocused(true)}
          onBlur={() => {
            setFocused(false)
            onBlurValidate?.()
          }}
          onChange={(event) => {
            const input = event.target
            const selection = input.selectionStart ?? input.value.length
            const nextDigits = extractUaNationalDigits(input.value).slice(0, UA_NATIONAL_LENGTH)
            const digitsBeforeCaret = countDigitsBeforeIndex(input.value, selection)

            // Prefer digit-index caret; when appending, park after the new last digit.
            const grew = nextDigits.length > digits.length
            const wasAtEnd = selection >= String(input.value).length
            pendingCaretDigitsRef.current =
              grew && wasAtEnd ? nextDigits.length : Math.min(digitsBeforeCaret, nextDigits.length)

            onChange(buildUaPhoneValue(nextDigits))
          }}
        />
        <label htmlFor={id}>{label}</label>
      </div>
      {error && !hideErrorMessage ? (
        <p className={styles.fieldError} id={errorId} role="alert">
          {error}
        </p>
      ) : null}
    </div>
  )
}
